import { buildShopTrustSummary } from "./shopTrustFormatting";

export function formatShopAddress(shop) {
  return [shop?.street_address, shop?.city, shop?.state, shop?.zip_code]
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join(", ");
}

export function formatShopLocation(shop, fallback = "") {
  if (shop?.city && shop?.state) return `${shop.city}, ${shop.state}`;
  return shop?.zip_code || fallback;
}

export function getShopGoogleMapsUrl(shop) {
  const address = formatShopAddress(shop);
  if (!address) return "";
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
}

export function normalizeShopSpecializations(shop) {
  const raw = shop?.shop_specializations ?? shop?.specializations ?? [];
  if (Array.isArray(raw)) {
    return raw.map((item) => String(item || "").trim()).filter(Boolean);
  }
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) return [];
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed.map((item) => String(item || "").trim()).filter(Boolean);
      }
    } catch {}
    return trimmed
      .replace(/^\{/, "")
      .replace(/\}$/, "")
      .split(",")
      .map((item) => item.trim().replace(/^"|"$/g, ""))
      .filter(Boolean);
  }
  return [];
}

export function buildShopSpecializationLabel(shop, effectiveMake = "") {
  const specializations = normalizeShopSpecializations(shop);
  if (specializations.length > 0) return `${specializations.join(", ")} specialist`;
  if (shop?.is_specialist_for_make) return `${effectiveMake || "Make"} specialist`;
  return "";
}

export function normalizeShopServices(shop) {
  const raw = shop?.shop_services ?? shop?.services ?? [];
  if (Array.isArray(raw)) {
    return raw.map((item) => String(item || "").trim()).filter(Boolean);
  }
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) return [];
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed.map((item) => String(item || "").trim()).filter(Boolean);
      }
    } catch {}
    return trimmed
      .replace(/^\{/, "")
      .replace(/\}$/, "")
      .split(",")
      .map((item) => item.trim().replace(/^"|"$/g, ""))
      .filter(Boolean);
  }
  return [];
}

export function buildShopServicesLabel(shop) {
  const services = normalizeShopServices(shop);
  if (services.length > 0) return `Services: ${services.join(", ")}`;
  return "";
}

// Shared predicate for the "Service (optional)" filter on both the car-request shop search
// (VehicleSelector) and Shop Explorer. Keep the two pages using THIS helper rather than
// duplicating the match logic - see the "Keep shop filters consistent" rule in
// CLAUDE.md / AGENTS.md. An empty/blank `service` means "no filter" (all shops pass).
export function shopOffersService(shop, service) {
  const target = String(service || "").trim().toLowerCase();
  if (!target) return true;
  return normalizeShopServices(shop).some((s) => s.toLowerCase() === target);
}

export function getShopBadges(shop, effectiveMake = "") {
  const badges = [];
  // First, so it is the first thing read on the card. Tells a demo visitor which shop in the
  // list is the one they can answer as after switching sides - without it they have no way to
  // know, and the send-as-owner / reply-as-shop round trip is the point of the demo.
  // The server sets is_own_demo_shop; it is always false outside a demo session.
  if (shop?.is_own_demo_shop) badges.push({ key: "own-demo-shop", label: "Your demo shop" });
  if (shop?.is_mobile_mechanic) {
    badges.push({ key: "mobile", label: "Mobile · comes to you" });
  }
  const specializationLabel = buildShopSpecializationLabel(shop, effectiveMake);
  if (specializationLabel) badges.push({ key: "specialization", label: specializationLabel });
  if (shop?.does_general_repair || shop?.shop_general_repair) {
    badges.push({ key: "general-repair", label: "General auto repair" });
  }
  return badges;
}

// Returns null when the server sent no fit score, so the card omits the line entirely - same
// contract as formatShopRating below. A score only means something relative to a search; the
// pinned card in an open thread has no make and no radius, so the server sends null rather than
// _compute_fit_score's floor. Coercing that to 0 would print "Match 0/100", which is worse than
// the misleading 40 this replaced.
function hasFitScore(shop) {
  // The == null test is load-bearing and must come first: Number(null) is 0, not NaN, so a
  // JSON null from the server would otherwise pass the isFinite check and print "Match 0/100".
  if (!shop || shop.fit_score == null) return false;
  return Number.isFinite(Number(shop.fit_score));
}

export function formatShopMatchScore(shop) {
  if (!hasFitScore(shop)) return null;
  return `Match ${Math.round(Number(shop.fit_score))}/100`;
}

export function formatShopFitReasons(shop) {
  if (Array.isArray(shop?.fit_reasons) && shop.fit_reasons.length > 0) {
    return shop.fit_reasons.join(" • ");
  }
  // Only fall back to the generic line when there IS a score to explain. With no score at all
  // there is nothing to justify, and the placeholder just reads as filler.
  if (!hasFitScore(shop)) return null;
  return "General fit based on location and service profile";
}

// Cached Google rating for the shop card. Returns null when there is no rating yet
// (the scheduler has not fetched one, or the Places feature is dormant), so the card
// simply omits the rating line.
export function formatShopRating(shop) {
  const rating = Number(shop?.google_rating);
  if (!shop || !Number.isFinite(rating) || rating <= 0) return null;
  const count = Number(shop?.google_review_count) || 0;
  return { rating: rating.toFixed(1), count };
}

export function getShopBusinessLinks(shop, mapsUrl = "") {
  return [
    // Mobile mechanics have no fixed storefront to map - drop the location link for them.
    { key: "map", type: "map", href: shop?.is_mobile_mechanic ? "" : mapsUrl, title: "Map", label: "Business location on map" },
    { key: "website", type: "website", href: shop?.business_website_url, title: "Website", label: "Business website" },
    { key: "facebook", type: "facebook", href: shop?.business_facebook_url, title: "Facebook", label: "Facebook page" },
    { key: "youtube", type: "youtube", href: shop?.business_youtube_url, title: "YouTube", label: "YouTube channel" },
    { key: "google", type: "google", href: shop?.business_google_profile_url, title: "Google", label: "Google Business Profile" },
  ].filter((link) => !!link.href);
}

export function buildShopPopupViewModel(shop, { effectiveMake = "", mapsUrl = "", isFavorite = false } = {}) {
  const resolvedMapsUrl = mapsUrl || getShopGoogleMapsUrl(shop);
  const badges = getShopBadges(shop, effectiveMake);
  return {
    mapsUrl: resolvedMapsUrl,
    badges,
    specializationBadge: badges.find((badge) => badge.key === "specialization")?.label || "",
    trustSummary: buildShopTrustSummary(shop),
    isFavorite,
  };
}
